import { useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import * as emitterEvents from 'constants/emitterEvents';
import * as socketEvents from 'constants/socketEvents';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import Emitter from 'services/eventEmitter';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  MessageSocketError,
} from 'store/reducers/atendimentos';
import {
  AcaoDeListaInterativa,
  addMensagemAtendimento,
  MensagemInterface,
  MensagemListaInterativaInterface,
} from 'store/reducers/mensagens';
import parseTextToHtml from 'utils/parseTextToHtml';

import DialogHeader from './DialogHeader';
import MessageBase, { MessageBaseProps } from './MessageBase';
import MessageErrorFallback from './MessageErrorFallback';
import ReadMoreParsedText from './ReadMoreParsedText';

const useStyles = makeStyles(({ palette, spacing }) => ({
  divider: {
    backgroundColor: palette.text.secondary,
    marginLeft: -16,
    marginRight: -16,
  },
  readMoreButton: {
    paddingBlock: 0,
    minWidth: 'fit-content',
    textTransform: 'none',
    alignSelf: 'flex-start',
    color: palette.text.secondary,
  },
  listItem: {
    padding: spacing(1.5),
    borderRadius: spacing(1.5),
    height: 'fit-content',

    '&:hover': {
      backgroundColor: palette.action.hover,
      cursor: 'pointer',
    },
  },
  actionButton: {
    color: 'inherit',
  },
}));

type InteractiveListMessageProps = MessageBaseProps;

function InteractiveListMessage(props: InteractiveListMessageProps) {
  const classes = useStyles();

  const searchQuery = useSearchQuery();
  const [mensagem, setMensagem] = useState<MensagemInterface>();
  const [action, setAction] = useState<AcaoDeListaInterativa>();

  const [showModalInteractiveList, hideModalInteractiveList] = useModal(
    ({ in: open }) => {
      if (!mensagem || !action) return null;

      return (
        <ModalInteractiveList
          mensagem={mensagem}
          action={action}
          open={open}
          onClose={hideModalInteractiveList}
        />
      );
    },
    [mensagem, action],
  );

  const handleShowModalInteractiveList = (
    mensagem: MensagemInterface,
    action: AcaoDeListaInterativa,
  ) => {
    setMensagem(mensagem);
    setAction(action);
    showModalInteractiveList();
  };

  try {
    const { mensagem } = props;
    const { whatsappInteractiveComponents } = mensagem;

    const { header, body, footer, action } =
      whatsappInteractiveComponents as MensagemListaInterativaInterface;

    return (
      <MessageBase {...props}>
        <Box padding={1}>
          {/* Header text */}
          <Typography
            style={{ fontWeight: 'bold' }}
            variant="body2"
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(header?.text || '', searchQuery),
            }}
          />

          {/* Body text */}
          <ReadMoreParsedText texto={body.text || ''} variant="body2" />

          {/* Footer text */}
          <Typography
            style={{ marginTop: 8 }}
            variant="caption"
            color="textSecondary"
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(footer?.text || '', searchQuery),
            }}
          />
        </Box>

        <Box mt={1}>
          <Divider className={classes.divider} />
        </Box>

        <Box p={1}>
          <Button
            onClick={() => handleShowModalInteractiveList(mensagem, action)}
            startIcon={<FormatListBulletedIcon />}
            className={classes.actionButton}
            fullWidth
            size="small"
          >
            {action?.button}
          </Button>
        </Box>
      </MessageBase>
    );
  } catch (error) {
    return <MessageErrorFallback {...props} />;
  }
}

export default InteractiveListMessage;

type ModalInteractiveListProps = {
  mensagem: MensagemInterface;
  action: AcaoDeListaInterativa;
  open: boolean;
  onClose: () => void;
};

function ModalInteractiveList({
  mensagem,
  action,
  open,
  onClose,
}: ModalInteractiveListProps) {
  const { button, sections } = action;
  const classes = useStyles();
  const { atendimentoSelecionado } = useAppSelector(atendimentosSelector);
  const searchQuery = useSearchQuery();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const handleSendText = (texto: string) => {
    if (atendimentoSelecionado?.id !== mensagem.atendimentoId) {
      onClose();
      return;
    }
    const atendimentoId = atendimentoSelecionado?.id;
    const mensagemCitadaId = mensagem.id;

    if (atendimentoId) {
      const data = { atendimentoId, mensagem: texto, mensagemCitadaId };
      socket.emit(
        socketEvents.EMIT_ENVIAR_MENSAGEM,
        data,
        (msgEnviada: MensagemInterface | MessageSocketError) => {
          if ('error' in msgEnviada) {
            enqueueSnackbar(msgEnviada.error);
            return;
          }

          onClose();
          dispatch(addMensagemAtendimento(msgEnviada));

          Emitter.emit(
            emitterEvents.ENVIAR_NOVA_MENSAGEM_ATENDENTE_NO_SCROLL_TOP,
            true,
          );
        },
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogHeader title={button} onClose={onClose} />

      <DialogContent style={{ paddingInline: 8 }}>
        {sections.map(section => (
          <Box key={section.title} style={{ marginBottom: 16 }}>
            {/* Section title */}
            <Box>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ fontWeight: 'bold' }}
                dangerouslySetInnerHTML={{
                  __html: parseTextToHtml(section.title || '', searchQuery),
                }}
              />
            </Box>

            {section.rows.map(row => {
              return (
                <Box
                  key={row.id}
                  onClick={() =>
                    handleSendText(
                      `@sem-autor\n${row.title}\n${row.description}`,
                    )
                  }
                >
                  <ListItemText
                    className={classes.listItem}
                    primary={row.title}
                    secondary={row.description}
                    primaryTypographyProps={{
                      variant: 'body2',
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'textSecondary',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
}
